import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
} from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { formatDatePipe } from "@/core/pipes/format-date.pipe";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { Image } from "antd";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import { ExclamationCircleFilled } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { DEF_MONTH_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";

const waterIndexRepo = container.get(WaterIndexRepo);
const tollAreaRepo = container.get(TollAreaRepo);

export interface ExportInvoiceMonthQuery {
  month: string;
  year: string;
  tollAreaId?: string;
}

export interface _ExportInvoiceMonthQuery {
  date: Dayjs;
  tollAreaId?: string;
}

interface ExportInvoiceMonthModalProps {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  data: ExportInvoiceMonthQuery;
}

const INPUT_WIDTH = 450;

export const ExportInvoiceMonthModal: React.FC<
  ExportInvoiceMonthModalProps
> = ({ isModalOpen, setIsModalOpen, data }) => {
  const [form] = Form.useForm<_ExportInvoiceMonthQuery>();
  const [loading, setLoading] = useState<boolean>(false);
  const [tollAreas, setTollAreas] = useState<TollAreaModel[]>([]);
  const defaultFilter = GlobalUtils.getPreviousMonthFilter();
  const [month, setMonth] = useState<string>(defaultFilter.month.value);
  const [year, setYear] = useState<string>(defaultFilter.year.value);
  // Attach hooks to handle data changes
  const handleDataChange = async () => {
    const date = dayjs(new Date(parseInt(data.year), parseInt(data.month) - 1));
    setMonth(data.month);
    setYear(data.year);
    form.setFieldsValue({
      date: date,
      tollAreaId: data.tollAreaId,
    });
  };
  useEffect(() => {
    handleDataChange();
  }, [data]);

  const populateTollAreas = async () => {
    const _tollAreas = await tollAreaRepo.getAll();
    setTollAreas(_tollAreas);
  };

  // Initialize select options data
  const init = async () => {
    await populateTollAreas();
  };
  // componentDidMount equivalent, run exclusively on client-side
  useEffect(() => {
    init();
  }, []);

  const parseDateToMonthYear = (date: Dayjs) => {
    const month = (date.month() + 1).toString();
    const year = date.year().toString();
    return { month, year };
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = await form.validateFields();
      message.info({
        content: `Đang tạo bảng tính phí tháng ${month}/${year}. Vui lòng chờ xử lý xong`,
      });
      const result = await waterIndexRepo.exportInvoiceMonth(
        month,
        year,
        formData.tollAreaId
      );
      if (result?.code === "00") {
        message.success({
          content: `Tạo bảng tính phí thành công`,
        });
      } else {
        message.error({
          content: `${result?.errorMessage}`,
        });
      }
      setIsModalOpen(false);
    } catch (error) {
      message.error({
        content: `Tạo bảng tính phí tháng ${month}/${year} thất bại`,
      });
      console.log(error);
    }
    setLoading(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <ConfigProvider theme={MODAL_THEME}>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        width={510}
        centered
        styles={{
          mask: {
            backdropFilter: "blur(2px)",
            backgroundColor: "var(--primary-mask)",
          },
        }}
        footer={[
          <div key={1} className="w-full flex flex-row justify-end px-1.5">
            <Popconfirm
              title={`Tạo bảng tính phí?`}
              description={`Bạn có chắc chắn muốn tạo bảng tính phí tháng ${month}/${year}?`}
              onConfirm={handleSave}
              okText="Tạo bảng tính phí"
              cancelText="Hủy"
              okButtonProps={{
                style: { backgroundColor: "var(--primary-color)" },
              }}
              icon={
                <ExclamationCircleFilled
                  style={{ color: "var(--danger-color)" }}
                />
              }
            >
              <Button
                key="submit"
                type="primary"
                loading={loading}
                style={{
                  background: "var(--primary-color)",
                  paddingRight: 48,
                  paddingLeft: 48,
                  fontSize: 18,
                  fontWeight: 600,
                  paddingTop: 18,
                  paddingBottom: 18,
                }}
              >
                TẠO BẢNG TÍNH PHÍ
              </Button>
            </Popconfirm>
          </div>,
        ]}
      >
        <div className="flex flex-row w-full items-start justify-start select-none">
          <p className="ml-2 text-2xl font-bold text-primary">
                TẠO BẢNG TÍNH PHÍ
          </p>
        </div>
        <div className="flex flex-row justify-center mt-6">
          <Form<_ExportInvoiceMonthQuery>
            name="basic"
            className="flex flex-col items-end"
            form={form}
            layout="vertical"
          >
            <Form.Item<_ExportInvoiceMonthQuery>
              label="Tháng"
              name="date"
              getValueProps={(i) => ({ value: dayjsPipe(i) })}
            >
              <DatePicker.MonthPicker
                format={DEF_MONTH_FORMAT}
                onChange={(date) => {
                  const { month, year } = parseDateToMonthYear(date);
                  setMonth(month);
                  setYear(year);
                }}
                style={{ width: INPUT_WIDTH }}
              />
            </Form.Item>
            <Form.Item<_ExportInvoiceMonthQuery>
              label="Khu vực"
              name="tollAreaId"
            >
              <Select
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={tollAreas.map((tollArea, index) => ({
                  value: tollArea.id,
                  label: tollArea.name,
                }))}
                style={{ width: INPUT_WIDTH }}
              >
                <Select.Option key={0} value={undefined}>
                  Tất cả
                </Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </div>
        <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
      </Modal>
    </ConfigProvider>
  );
};
